/**
 * The press the page consumes because it closed a pinned tooltip on the world (P2.2-T12; the lead's ruling of
 * 2026-10-02, narrowed the same day: only a closing press that lands on the world is consumed, so it neither selects
 * nor swings; one on any HUD control closes the tooltip and does its own thing). It decides, event by event, which
 * events are that press's own: its pointer's release; its mouse events for a mouse or pen; for a touch, the events of
 * the touch identifier that follows it, never a second finger's. A click is never stopped: the game reads no click on
 * the canvas, and one may be another finger's tap or a key's.
 * The consumption ends a short tail after the release, at a ceiling if no release comes, and at once on a clear (the
 * page's blur or hiding). Pure: the page hands it plain descriptions of events and the time.
 */

/**
 * How long after the press's release its trailing events (a touch's end, the click) are still its own, in ms.
 */
export const CONSUME_TAIL_MS = 50;

/**
 * The longest a consumption lasts with no release seen, in ms.
 */
export const CONSUME_LIMIT_MS = 5000;

/**
 * An event as the filter reads it: its type, a pointer event's id, and a touch event's changed
 * touch identifiers.
 */
export interface PressEvent {
  readonly type: string;
  readonly pointerId?: number;
  readonly touches?: readonly number[];
}

/**
 * The consumed press.
 *
 * @example
 * ```ts
 * const press = new ConsumedPress();
 * press.begin("mouse", event.pointerId, performance.now());
 * if (press.stops({ type: "mousedown" }, performance.now())) event.stopPropagation();
 * ```
 */
export class ConsumedPress {
  private pointerType: string | null = null;
  private pointerId = -1;
  private touchId: number | null = null;
  private began = 0;
  private endsAt = Number.POSITIVE_INFINITY;

  /**
   * Whether a press is being consumed.
   *
   * @returns True while one is.
   */
  public get active(): boolean {
    return this.pointerType !== null;
  }

  /**
   * Starts consuming a press, whose pointer down the page has already stopped.
   *
   * @param pointerType Its pointer's type.
   * @param pointerId Its pointer's id.
   * @param now The page clock, in ms.
   */
  public begin(pointerType: string, pointerId: number, now: number): void {
    this.pointerType = pointerType;
    this.pointerId = pointerId;
    this.touchId = null;
    this.began = now;
    this.endsAt = Number.POSITIVE_INFINITY;
  }

  /**
   * Ends the consumption at once.
   */
  public clear(): void {
    this.pointerType = null;
    this.pointerId = -1;
    this.touchId = null;
    this.endsAt = Number.POSITIVE_INFINITY;
  }

  /**
   * Whether an event is the consumed press's own and must stop. Its pointer's release starts the tail.
   *
   * @param event The event.
   * @param now The page clock, in ms.
   * @returns True when the page must stop it.
   */
  public stops(event: PressEvent, now: number): boolean {
    if (this.pointerType === null) {
      return false;
    }
    if (now >= this.endsAt || now - this.began >= CONSUME_LIMIT_MS) {
      this.clear();
      return false;
    }
    switch (event.type) {
      case "pointerup":
      case "pointercancel":
        if (event.pointerId !== this.pointerId) {
          return false;
        }
        this.endsAt = now + CONSUME_TAIL_MS;
        return true;
      case "pointermove":
        return event.pointerId === this.pointerId;
      case "mousedown":
      case "mouseup":
      case "mousemove":
        return this.pointerType === "mouse" || this.pointerType === "pen";
      case "touchstart":
        if (this.pointerType !== "touch") {
          return false;
        }
        if (this.touchId === null && event.touches?.length === 1) {
          this.touchId = event.touches[0] as number;
          return true;
        }
        return this.onlyOwnTouch(event);
      case "touchmove":
      case "touchend":
      case "touchcancel":
        return this.onlyOwnTouch(event);
      default:
        return false;
    }
  }

  /**
   * Whether a touch event changes the consumed touch alone.
   *
   * @param event The event.
   * @returns True when every changed touch is the consumed one.
   */
  private onlyOwnTouch(event: PressEvent): boolean {
    const touches = event.touches ?? [];
    return this.touchId !== null && touches.length > 0 && touches.every((id) => id === this.touchId);
  }
}
